import { tokenManager } from "./tokenManager";
import { userTimezoneHeaders } from "./userTimezoneHeader";

const ALERT_SERVICE_BASE = (process.env.REACT_APP_API_URL || "").replace(
  /\/$/,
  "",
);
const AI_DETECTION_BASE = (
  process.env.REACT_APP_API_URL_FOR_AI_DETECTION || ""
).replace(/\/$/, "");

/** GET `/detections/{id}/photo|thumbnail|video` — served by AI detection API, not alert service. */
function isCanonicalDetectionMediaPath(pathOrUrl) {
  const s = String(pathOrUrl || "").trim();
  if (!s) return false;
  return /^\/?detections\/[^/]+\/(photo|video|thumbnail)(\/|$|\?)/i.test(s);
}

function baseForMediaPath(pathOrUrl) {
  if (isCanonicalDetectionMediaPath(pathOrUrl) && AI_DETECTION_BASE) {
    return AI_DETECTION_BASE;
  }
  return ALERT_SERVICE_BASE;
}

/**
 * Absolute URL for alert history media. Canonical `/detections/{id}/photo|thumbnail|video`
 * uses `REACT_APP_API_URL_FOR_AI_DETECTION`; other relative paths use `REACT_APP_API_URL`.
 * @param {string} pathOrUrl
 * @returns {string}
 */
export function resolveServiceworkerMediaPath(pathOrUrl) {
  const u = (pathOrUrl || "").trim();
  if (!u || u.toLowerCase() === "null") return "";
  if (/^https?:\/\//i.test(u)) return u;
  const base = baseForMediaPath(u);
  if (!base) return u.startsWith("/") ? u : `/${u}`;
  return u.startsWith("/") ? `${base}${u}` : `${base}/${u}`;
}

/**
 * True when the URL is on an API host that requires Bearer auth for media (alert or AI detection).
 * @param {string} absoluteUrl
 * @returns {boolean}
 */
export function needsServiceworkerAuthFetch(absoluteUrl) {
  if (!absoluteUrl) return false;
  if (absoluteUrl.startsWith("blob:") || absoluteUrl.startsWith("data:")) {
    return false;
  }
  if (ALERT_SERVICE_BASE && absoluteUrl.startsWith(ALERT_SERVICE_BASE)) {
    return true;
  }
  if (AI_DETECTION_BASE && absoluteUrl.startsWith(AI_DETECTION_BASE)) {
    return true;
  }
  return false;
}

/**
 * Paths for GET `/detections/{detection_id}/photo|video|thumbnail` (AI detection API base).
 * List/history may return these as `photo_url`, `video_url`, `thumbnail_url`.
 *
 * @param {string} detectionId
 * @param {"photo" | "video" | "thumbnail"} kind
 * @returns {string} path like `/detections/{uuid}/photo`
 */
export function detectionMediaPath(detectionId, kind) {
  const id = String(detectionId || "").trim();
  if (!id) return "";
  return `/detections/${id}/${kind}`;
}

/**
 * GET media; supports image/video bodies or JSON body containing a string URL/path.
 * @param {string} absoluteUrl
 * @param {number} [depth]
 * @returns {Promise<string>} blob: URL or absolute https URL usable without extra auth
 */
export async function fetchServiceworkerMediaBlobUrl(absoluteUrl, depth = 0) {
  if (!absoluteUrl || depth > 4) {
    throw new Error("Invalid media URL");
  }
  const token = tokenManager.getToken();
  const res = await fetch(absoluteUrl, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...userTimezoneHeaders(),
    },
  });
  if (!res.ok) {
    throw new Error(`Media request failed (${res.status})`);
  }
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  if (ct.includes("application/json")) {
    const text = await res.text();
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      return "";
    }
    const inner =
      typeof parsed === "string"
        ? parsed.trim()
        : parsed && typeof parsed.url === "string"
          ? parsed.url.trim()
          : "";
    if (!inner) return "";
    if (/^https?:\/\//i.test(inner)) {
      if (inner === absoluteUrl) return inner;
      return fetchServiceworkerMediaBlobUrl(inner, depth + 1);
    }
    const next = resolveServiceworkerMediaPath(inner);
    if (next === absoluteUrl) return next;
    return fetchServiceworkerMediaBlobUrl(next, depth + 1);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}
